import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import AppIcon from '../../../../components/AppIcon';
import ReflectionProgress from './ReflectionProgress';
import ReflectionPromptCard from './ReflectionPromptCard';
import ManagerResponseCard from './ManagerResponseCard';
import PreviousReflections from './PreviousReflections';
import type { ReflectionData, ManagerResponseData, PreviousReflectionData } from '../types';

interface ReflectionContentProps {
  reflectionData: ReflectionData;
  currentMonthManagerResponse: ManagerResponseData | null;
  previousReflectionsData: PreviousReflectionData[];
  completedCount: number;
  totalCount: number;
  isAllComplete: boolean;
  canSubmit: boolean;
  deadlinePassed: boolean;
  onSave: (promptId: string, response: string) => Promise<void>;
  onSubmit: (promptId: string, response: string) => Promise<void>;
}

const ReflectionContent: React.FC<ReflectionContentProps> = ({
  reflectionData,
  currentMonthManagerResponse,
  previousReflectionsData,
  completedCount,
  totalCount,
  isAllComplete,
  canSubmit,
  deadlinePassed,
  onSave,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const hasManagerResponse = currentMonthManagerResponse !== null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6">
      {/* Left Column */}
      <div>
        <ReflectionProgress
          totalPrompts={totalCount}
          completedPrompts={completedCount}
          currentMonth={reflectionData.currentMonth}
        />

        <div className="mt-6">
          {reflectionData.prompts.map((prompt, index) => {
            const isSubmitted = reflectionData.submittedPrompts.includes(prompt.id);
            const canSave = !deadlinePassed && !hasManagerResponse;
            const canSubmitThisPrompt = canSubmit && !isSubmitted && !hasManagerResponse;

            return (
              <ReflectionPromptCard
                key={prompt.id}
                prompt={prompt}
                response={reflectionData.responses[prompt.id] || ''}
                isSubmitted={isSubmitted}
                onSave={onSave}
                onSubmit={onSubmit}
                index={index}
                totalPrompts={totalCount}
                canSave={canSave}
                canSubmit={canSubmitThisPrompt}
                deadlinePassed={deadlinePassed}
              />
            );
          })}
        </div>

        {isAllComplete && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-500 rounded-xl p-8 text-center mt-6">
              <div className="w-16 h-16 bg-emerald-500 rounded-full mx-auto mb-6 flex items-center justify-center">
                <AppIcon name="CheckCircle" size={32} className="text-white" />
              </div>
              <h3 className="text-xl font-semibold text-emerald-800 dark:text-emerald-200 mb-2">
                {t('performance.reflections.reflectionComplete')}
              </h3>
              <p className="text-sm text-emerald-700 dark:text-emerald-300 opacity-90">
                {t('performance.reflections.reflectionCompleteDesc')}
              </p>
            </div>
          </motion.div>
        )}
      </div>

      {/* Right Column */}
      <div className="flex flex-col gap-6">
        <ManagerResponseCard response={currentMonthManagerResponse} />
        <PreviousReflections reflections={previousReflectionsData} />
      </div>
    </div>
  );
};

export default ReflectionContent;
